import React, { useEffect, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { Form, Overlay, Popover } from 'react-bootstrap';

import { AudioTrack } from './trackList';

interface Props {
  track: AudioTrack | null;
  videoUrl: string | null;
  audioVolume: number;
  videoVolume: number;
  onAudioVolumeChange?: (value: number) => void;
  onVideoVolumeChange?: (value: number) => void;
}

export const TrackPreview: React.FC<Props> = ({
  track,
  videoUrl,
  audioVolume,
  videoVolume,
  onAudioVolumeChange,
  onVideoVolumeChange
}) => {
  const { t } = useTranslate();

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showAudioPopover, setShowAudioPopover] = useState(false);
  const [showVideoPopover, setShowVideoPopover] = useState(false);
  const audioBadgeRef = useRef<HTMLButtonElement | null>(null);
  const videoBadgeRef = useRef<HTMLButtonElement | null>(null);

  // Close popovers on outside click without using OverlayTrigger's rootClose
  // (which can race with the badge's own onClick and close immediately).
  useEffect(() => {
    if (!showAudioPopover && !showVideoPopover) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        showAudioPopover &&
        audioBadgeRef.current &&
        !audioBadgeRef.current.contains(target) &&
        !(target as HTMLElement).closest?.('.ig-music-audio-popover')
      ) {
        setShowAudioPopover(false);
      }
      if (
        showVideoPopover &&
        videoBadgeRef.current &&
        !videoBadgeRef.current.contains(target) &&
        !(target as HTMLElement).closest?.('.ig-music-video-popover')
      ) {
        setShowVideoPopover(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAudioPopover, showVideoPopover]);

  // Keep volume controls in sync with media elements. Depend on the source
  // URLs too so the volume is (re)applied when the elements first mount.
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = Math.max(0, Math.min(1, videoVolume / 100));
    }
  }, [videoVolume, videoUrl]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = Math.max(0, Math.min(1, audioVolume / 100));
    }
  }, [audioVolume, track?.download_url]);

  // Reset playback to the start whenever the selected track or the video
  // changes. When a track is selected, auto-play the preview so the user
  // immediately hears how the song sounds with their video.
  useEffect(() => {
    const video = videoRef.current;
    const audio = audioRef.current;

    if (video) {
      video.pause();
      video.currentTime = 0;
    }
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      // Force the new src to load before play() to avoid stale buffers.
      try {
        audio.load();
      } catch (_) {
        /* noop */
      }
    }

    if (track?.audio_id && videoUrl) {
      // Browsers allow play() when triggered from a user gesture (the click
      // on the track row). Swallow rejections (autoplay policy / no source).
      if (video) {
        void video.play().catch(() => undefined);
      }
      if (audio) {
        void audio.play().catch(() => undefined);
      }
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }
  }, [track?.audio_id, videoUrl]);

  const handleToggle = () => {
    const video = videoRef.current;
    const audio = audioRef.current;

    if (isPlaying) {
      video?.pause();
      audio?.pause();
      setIsPlaying(false);
      return;
    }

    if (video) {
      void video.play().catch(() => undefined);
    }
    if (audio) {
      void audio.play().catch(() => undefined);
    }
    setIsPlaying(true);
  };

  const handleVideoEnded = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
  };

  return (
    <div className="d-flex flex-column gap-3">
      <div
        role={videoUrl ? 'button' : undefined}
        tabIndex={videoUrl ? 0 : undefined}
        onClick={videoUrl ? handleToggle : undefined}
        onKeyDown={(e) => {
          if (!videoUrl) return;
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            handleToggle();
          }
        }}
        aria-label={
          videoUrl
            ? isPlaying
              ? t('lomavisCreator.IG.music.pausePreview')
              : t('lomavisCreator.IG.music.playPreview')
            : undefined
        }
        className="bg-black rounded position-relative d-flex justify-content-center align-items-center"
        style={{
          aspectRatio: '9 / 16',
          width: '100%',
          overflow: 'hidden',
          cursor: videoUrl ? 'pointer' : 'default'
        }}
      >
        {videoUrl ? (
          <video
            ref={videoRef}
            src={videoUrl}
            playsInline
            onEnded={handleVideoEnded}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              background: 'black'
            }}
          />
        ) : (
          <div className="text-white text-center small px-3">
            {t('lomavisCreator.IG.music.previewNoVideo')}
          </div>
        )}

        {videoUrl && (
          <div
            className="position-absolute d-flex align-items-center gap-2"
            style={{ top: 10, left: 10, zIndex: 2 }}
          >
            <button
              ref={audioBadgeRef}
              type="button"
              className="badge bg-white text-dark d-flex align-items-center gap-1 shadow-sm border-0"
              style={{ borderRadius: 6 }}
              aria-label={t(
                'lomavisCreator.IG.music.audioVolume',
                'Audio volume'
              )}
              onClick={(e) => {
                e.stopPropagation();
                setShowVideoPopover(false);
                setShowAudioPopover((prev) => !prev);
              }}
            >
              <i className="fa-duotone fa-music"></i>
              <span>{audioVolume}%</span>
            </button>
            <Overlay
              show={showAudioPopover}
              target={audioBadgeRef.current}
              placement="bottom"
              container={audioBadgeRef.current?.parentElement ?? undefined}
            >
              <Popover
                className="ig-music-audio-popover"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <Popover.Header as="h6" className="py-1 px-2 small">
                  {t('lomavisCreator.IG.music.audioVolume', 'Audio volume')}
                </Popover.Header>
                <Popover.Body className="py-2 px-2" style={{ width: 200 }}>
                  <div className="d-flex justify-content-between small mb-1">
                    <span>{audioVolume}%</span>
                  </div>
                  <Form.Range
                    min={0}
                    max={100}
                    value={audioVolume}
                    onChange={(e) =>
                      onAudioVolumeChange?.(Number(e.target.value))
                    }
                    disabled={!track || !onAudioVolumeChange}
                  />
                </Popover.Body>
              </Popover>
            </Overlay>

            <button
              ref={videoBadgeRef}
              type="button"
              className="badge bg-white text-dark d-flex align-items-center gap-1 shadow-sm border-0"
              style={{ borderRadius: 6 }}
              aria-label={t(
                'lomavisCreator.IG.music.videoVolume',
                'Video volume'
              )}
              onClick={(e) => {
                e.stopPropagation();
                setShowAudioPopover(false);
                setShowVideoPopover((prev) => !prev);
              }}
            >
              <i className="fa-duotone fa-volume-high"></i>
              <span>{videoVolume}%</span>
            </button>
            <Overlay
              show={showVideoPopover}
              target={videoBadgeRef.current}
              placement="bottom"
              container={videoBadgeRef.current?.parentElement ?? undefined}
            >
              <Popover
                className="ig-music-video-popover"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <Popover.Header as="h6" className="py-1 px-2 small">
                  {t('lomavisCreator.IG.music.videoVolume', 'Video volume')}
                </Popover.Header>
                <Popover.Body className="py-2 px-2" style={{ width: 200 }}>
                  <div className="d-flex justify-content-between small mb-1">
                    <span>{videoVolume}%</span>
                  </div>
                  <Form.Range
                    min={0}
                    max={100}
                    value={videoVolume}
                    onChange={(e) =>
                      onVideoVolumeChange?.(Number(e.target.value))
                    }
                    disabled={!onVideoVolumeChange}
                  />
                </Popover.Body>
              </Popover>
            </Overlay>
          </div>
        )}

        {videoUrl && (
          <i
            aria-hidden="true"
            className={`fa-duotone position-absolute text-white ${
              isPlaying ? 'fa-circle-pause' : 'fa-circle-play'
            }`}
            style={{
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: 64,
              lineHeight: 1,
              pointerEvents: 'none'
            }}
          ></i>
        )}

        {track?.download_url && (
          <audio
            ref={audioRef}
            src={track.download_url}
            preload="auto"
            onEnded={() => setIsPlaying(false)}
          />
        )}
      </div>

      {track && (
        <div className="d-flex align-items-center gap-2">
          {track.cover_artwork_thumbnail_uri ? (
            <img
              src={track.cover_artwork_thumbnail_uri}
              alt=""
              width={48}
              height={48}
              style={{ objectFit: 'cover', borderRadius: 6 }}
            />
          ) : (
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 6,
                background: '#eee'
              }}
              className="d-flex align-items-center justify-content-center"
            >
              <i className="fa-duotone fa-music text-muted"></i>
            </div>
          )}
          <div className="d-flex flex-column overflow-hidden">
            <span className="fw-semibold text-truncate">
              {track.title || track.ig_username || '—'}
            </span>
            <span className="text-muted small text-truncate">
              {track.display_artist || track.ig_username || ''}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
